import { useEffect, useId, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { getOrCreateJourneyId, getGrowthJourneyContext } from "@/utils/growthJourney";
import { trackClick } from "@/utils/posthog";
import {
  flushPendingKitConversion,
  queueKitConversion,
} from "@/utils/kit-conversion";
import {
  getJourneyCampaignContext,
  getJourneyHomeRevision,
  getOrganizationJourneyContext,
  rememberLibraryConfirmationJourney,
  type OrganizationJourneyContext,
} from "@/utils/organizationJourney";

/**
 * Inline Kit (ConvertKit) opt-in form.
 *
 * Kit inline embeds work by injecting a <script data-uid="..."> which replaces
 * itself with a real <form> in the DOM. Two consequences we handle here:
 *
 *  1. The script must be appended to a container that is already mounted, and
 *     only once — re-appending duplicates the form.
 *  2. Kit emits `ckjs:submission:complete` only after its subscription request
 *     succeeds. Redirecting forms queue that confirmed conversion in
 *     sessionStorage so it can be sent after the same-origin confirmation page
 *     loads instead of being lost during navigation.
 */
type KitOptInFormProps = {
  /** Kit form uid, e.g. "fb2b5743bc" for the Fortune 100 AI Prompt Library. */
  uid: string;
  /** Conversion name reported to PostHog after Kit confirms the subscription. */
  conversionName: string;
  /** Where on the page this form lives, for conversion attribution. */
  location: string;
  /** Page to send people to if the Kit script never renders a form. */
  fallbackHref: string;
  fallbackLabel: string;
  /** Same-origin path Kit redirects to after a successful subscription. */
  confirmationPath?: string;
  /** Explicit buyer context for a form on the organizational homepage. */
  journeyContext?: OrganizationJourneyContext;
  /** Page-specific copy applied to the native embed after it renders. */
  submitLabel?: string;
  firstNameOptional?: boolean;
  showFieldLabels?: boolean;
  className?: string;
};

// How long to wait for Kit to replace the injected script with a real form
// before falling back to a plain link. Ad blockers and script failures would
// otherwise leave the opt-in card with nothing to click.
const FALLBACK_DELAY_MS = 4000;

const KitOptInForm = ({
  uid,
  conversionName,
  location,
  fallbackHref,
  fallbackLabel,
  confirmationPath,
  journeyContext,
  submitLabel,
  firstNameOptional = false,
  showFieldLabels = false,
  className,
}: KitOptInFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldIdPrefix = useId();
  const [showFallback, setShowFallback] = useState(false);
  const journeyAudience = journeyContext?.audience;
  const homeRevision = journeyContext?.home_revision;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || (!submitLabel && !firstNameOptional && !showFieldLabels)) {
      return;
    }

    // Keep Kit's form, spinner, request, and confirmed-submission handling.
    // These opt-in overrides affect this embed only, not the provider's saved form.
    const customizeForm = () => {
      const form = container.querySelector("form");
      if (!form) return;

      const submitText = form.querySelector('button[data-element="submit"] > span');
      if (submitLabel && submitText) submitText.textContent = submitLabel;

      const fields = [
        {
          name: "fields[first_name]",
          label: firstNameOptional ? "First name (optional)" : "First name",
          autocomplete: "given-name",
          suffix: "first-name",
        },
        {
          name: "email_address",
          label: "Email address",
          autocomplete: "email",
          suffix: "email",
        },
      ] as const;
      for (const field of fields) {
        const input = form.querySelector<HTMLInputElement>(`input[name="${field.name}"]`);
        if (!input) continue;
        if (field.suffix === "first-name" && firstNameOptional) {
          input.required = false;
        }
        if (!showFieldLabels) continue;

        input.autocomplete = field.autocomplete;
        input.setAttribute("aria-label", field.label);
        if (field.suffix === "email") input.type = "email";
        if (input.labels?.length) continue;

        input.id ||= `${fieldIdPrefix}-${field.suffix}`;
        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "formkit-field-label";
        label.textContent = field.label;
        input.before(label);
      }
      // Leave subsequent loading, error, and success states to Kit.
      observer.disconnect();
    };
    const observer = new MutationObserver(customizeForm);
    observer.observe(container, { childList: true, subtree: true });
    customizeForm();
    return () => observer.disconnect();
  }, [submitLabel, firstNameOptional, showFieldLabels, fieldIdPrefix]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!container.querySelector("script") && !container.querySelector("form")) {
      const script = document.createElement("script");
      script.async = true;
      script.setAttribute("data-uid", uid);
      script.src = `https://techleaders.kit.com/${uid}/index.js`;
      container.appendChild(script);
    }

    let retryTimer: number | undefined;
    let retryAttempts = 0;

    const handleSubmit = () => { getOrCreateJourneyId(); };
    container.addEventListener("submit", handleSubmit, true);

    const handleComplete = (event: Event) => {
      const kitEvent = event as CustomEvent<{ uid?: string }>;
      if (kitEvent.detail?.uid && kitEvent.detail.uid !== uid) return;
      getOrCreateJourneyId();

      const organizationContext = journeyAudience === "organization"
        ? { audience: journeyAudience, ...(homeRevision ? { home_revision: homeRevision } : {}) }
        : getOrganizationJourneyContext(window.location.search);
      if (confirmationPath) {
        rememberLibraryConfirmationJourney(organizationContext, confirmationPath, window.location.search);
      }

      queueKitConversion(
        conversionName,
        {
          ...getGrowthJourneyContext(window.location.search),
          source: "kit_form",
          kit_form_uid: uid,
          location,
          source_page_path: window.location.pathname,
          audience: organizationContext ? "organization" : "individual",
          home_revision: organizationContext?.home_revision || getJourneyHomeRevision(window.location.search),
          ...getJourneyCampaignContext(window.location.search),
        },
        confirmationPath,
      );

      // Non-redirecting forms can send immediately. If PostHog is still
      // initializing, keep the confirmed event queued and retry briefly.
      if (confirmationPath) return;

      if (flushPendingKitConversion() === "waiting_for_posthog") {
        window.clearInterval(retryTimer);
        retryTimer = window.setInterval(() => {
          retryAttempts += 1;
          const result = flushPendingKitConversion();
          if (result !== "waiting_for_posthog" || retryAttempts >= 100) {
            window.clearInterval(retryTimer);
          }
        }, 100);
      }
    };
    container.addEventListener("ckjs:submission:complete", handleComplete);

    // Watch for Kit swapping the script out for the real form. Without this a
    // form that simply loads slowly would leave the fallback link showing
    // underneath it, so the card would offer the same thing twice.
    const observer = new MutationObserver(() => {
      if (container.querySelector("form")) {
        setShowFallback(false);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    const fallbackTimer = window.setTimeout(() => {
      setShowFallback(!container.querySelector("form"));
    }, FALLBACK_DELAY_MS);

    return () => {
      container.removeEventListener("ckjs:submission:complete", handleComplete);
      container.removeEventListener("submit", handleSubmit, true);
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
      window.clearInterval(retryTimer);
    };
  }, [uid, conversionName, location, confirmationPath, journeyAudience, homeRevision]);

  return (
    <>
      <div ref={containerRef} className={className} />
      {showFallback && (
        <a
          href={fallbackHref}
          className="inline-flex items-center justify-center w-full rounded-lg px-6 py-3 font-semibold text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 transition-all"
          onClick={() =>
            trackClick(`${conversionName} - Fallback Link`, {
              location,
              destination: fallbackHref,
            })
          }
        >
          {fallbackLabel}
          <ArrowRight className="ml-2 h-4 w-4" />
        </a>
      )}
    </>
  );
};

export default KitOptInForm;
